<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h2 :class="`text-2xl font-bold ${applyThemeClasses('text.primary')}`">
        💰 Active Bids
      </h2>
      <div v-if="bidSummary.totalActiveBids > 0" :class="`text-sm ${applyThemeClasses('text.secondary')}`">
        {{ bidSummary.totalActiveBids }} active bid{{ bidSummary.totalActiveBids === 1 ? '' : 's' }} 
        on {{ bidSummary.chocobosWithBids }} chocobo{{ bidSummary.chocobosWithBids === 1 ? '' : 's' }}
      </div>
    </div>

    <!-- Expiring Soon Alert -->
    <div v-if="bidSummary.expiringToday > 0" class="bg-yellow-100 border-l-4 border-yellow-500 p-4 rounded">
      <div class="flex items-center">
        <span class="text-yellow-600 text-lg mr-2">⚠️</span>
        <div>
          <h4 class="font-semibold text-yellow-800">Bids Expiring Soon!</h4>
          <p class="text-sm text-yellow-700">
            {{ bidSummary.expiringToday }} bid{{ bidSummary.expiringToday === 1 ? '' : 's' }} expire{{ bidSummary.expiringToday === 1 ? 's' : '' }} tomorrow
          </p>
        </div>
      </div>
    </div>

    <!-- No Bids Message -->
    <div v-if="activeBids.length === 0" :class="`text-center py-12 ${applyThemeClasses('layout.card')} rounded-lg`">
      <div class="text-6xl mb-4">🏠</div>
      <h3 :class="`text-xl font-semibold mb-2 ${applyThemeClasses('text.primary')}`">
        No Active Bids
      </h3>
      <p :class="`${applyThemeClasses('text.secondary')}`">
        Other owners will occasionally make offers on your chocobos. Check back later!
      </p>
    </div>

    <!-- Active Bids List -->
    <div v-else class="space-y-4">
      <div 
        v-for="bid in sortedBids" 
        :key="bid.id"
        :class="`rounded-lg p-6 border shadow-sm ${applyThemeClasses('layout.card')}`"
      >
        <!-- Chocobo and Bidder Info -->
        <div class="flex items-start justify-between mb-4">
          <div class="flex items-center space-x-4">
            <!-- Chocobo Info -->
            <div>
              <h3 :class="`text-lg font-semibold ${applyThemeClasses('text.primary')}`">
                {{ getChocoboName(bid.chocoboId) }}
              </h3>
              <p :class="`text-sm ${applyThemeClasses('text.secondary')}`">
                {{ getChocoboDetails(bid.chocoboId) }}
              </p>
            </div>
          </div>
          
          <!-- Bid Amount -->
          <div class="text-right">
            <div :class="`text-2xl font-bold ${applyThemeClasses('text.money')}`">
              {{ bid.amount.toLocaleString() }}G
            </div>
            <div :class="`text-sm ${applyThemeClasses('text.secondary')}`">
              Bid Amount
            </div>
          </div>
        </div>

        <!-- Bidder Info -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <span :class="`font-medium ${applyThemeClasses('text.secondary')}`">Bidder:</span>
            <span :class="`ml-2 ${applyThemeClasses('text.primary')}`">
              {{ bid.bidderName }}
            </span>
            <span :class="`text-sm ${applyThemeClasses('text.tertiary')}`">
              from {{ bid.bidderRanchName }}
            </span>
          </div>
          
          <!-- Expiration Info -->
          <div class="text-right">
            <div :class="`text-sm font-medium ${getExpirationColor(bid)}`">
              {{ getExpirationText(bid) }}
            </div>
            <div :class="`text-xs ${applyThemeClasses('text.tertiary')}`">
              Created {{ formatNotificationDate(bid.createdDay).date }}
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex space-x-3 pt-4 border-t">
          <button
            @click="acceptBid(bid)"
            :disabled="isProcessing"
            class="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {{ isProcessing ? 'Processing...' : 'Accept Bid' }}
          </button>
          
          <div class="flex-1 relative">
            <button
              @click="showRejectOptions(bid)"
              :disabled="isProcessing"
              class="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
            >
              {{ isProcessing ? 'Processing...' : 'Reject Bid' }}
              <span class="ml-1">▼</span>
            </button>
            
            <!-- Reject Options Dropdown -->
            <div 
              v-if="showingRejectOptions === bid.id" 
              class="absolute top-full left-0 right-0 mt-1 bg-white border rounded shadow-lg z-10"
            >
              <button
                @click="rejectBid(bid, false)"
                :disabled="isProcessing"
                class="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 disabled:opacity-50"
              >
                Reject this bid
              </button>
              <button
                @click="rejectBid(bid, true)"
                :disabled="isProcessing"
                class="w-full px-4 py-2 text-left hover:bg-gray-50 text-red-600 border-t disabled:opacity-50"
              >
                Reject & mark not for sale
              </button>
            </div>
          </div>
          
          <button
            @click="viewChocobo(bid.chocoboId)"
            :class="`px-4 py-2 rounded font-medium ${applyThemeClasses('form.button.outline')}`"
          >
            View Details
          </button>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div v-if="recentBidActivity.length > 0" class="mt-8">
      <h3 :class="`text-lg font-semibold mb-4 ${applyThemeClasses('text.primary')}`">
        Recent Bid Activity
      </h3>
      <div :class="`rounded-lg p-4 ${applyThemeClasses('layout.card')}`">
        <div class="space-y-2 max-h-40 overflow-y-auto">
          <div 
            v-for="activity in recentBidActivity.slice(0, 5)"
            :key="activity.id"
            :class="`text-sm flex justify-between ${applyThemeClasses('text.secondary')}`"
          >
            <span>{{ activity.message }}</span>
            <span :class="`text-xs ${applyThemeClasses('text.tertiary')}`">
              {{ formatNotificationDate(activity.day).date }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, defineEmits, onMounted, onUnmounted } from 'vue'
import { useGameStore } from '@/store/game'
import { BidManager } from '@/services/bidManager'
import { applyThemeClasses } from '@/utils/themeColors'
import { GaiaCalendar } from '@/utils/gaiaCalendar'

const emit = defineEmits(['view-chocobo'])
const gameStore = useGameStore()
const isProcessing = ref(false)
const showingRejectOptions = ref(null)

const activeBids = computed(() => {
  return BidManager.getActiveBids(gameStore)
})

const bidSummary = computed(() => {
  return BidManager.getBidSummary(gameStore)
})

const sortedBids = computed(() => {
  return [...activeBids.value].sort((a, b) => {
    // Sort by expiration (soonest first), then by amount (highest first)
    const aDaysLeft = a.expiresDay - gameStore.currentDay
    const bDaysLeft = b.expiresDay - gameStore.currentDay
    
    if (aDaysLeft !== bDaysLeft) return aDaysLeft - bDaysLeft
    return b.amount - a.amount
  })
})

const recentBidActivity = computed(() => {
  if (!gameStore.activeBids) return []
  
  return gameStore.activeBids
    .filter(bid => bid.status !== 'pending')
    .map(bid => ({
      id: bid.id,
      message: getBidActivityMessage(bid),
      day: bid.acceptedDay || bid.rejectedDay || bid.createdDay
    }))
    .sort((a, b) => b.day - a.day)
})

const getChocoboName = (chocoboId) => {
  const chocobo = gameStore.chocobos.find(c => c.id === chocoboId)
  return chocobo?.name || 'Unknown Chocobo'
}

const getChocoboDetails = (chocoboId) => {
  const chocobo = gameStore.chocobos.find(c => c.id === chocoboId)
  if (!chocobo) return 'Details unavailable'
  
  return `Level ${chocobo.level} ${chocobo.color.charAt(0).toUpperCase() + chocobo.color.slice(1)} • Speed: ${chocobo.speed} • Stamina: ${chocobo.stamina}`
}

const getExpirationText = (bid) => {
  const daysLeft = bid.expiresDay - gameStore.currentDay
  if (daysLeft <= 0) return 'Expired'
  if (daysLeft === 1) return 'Expires tomorrow'
  return `Expires in ${daysLeft} days`
}

const getExpirationColor = (bid) => {
  const daysLeft = bid.expiresDay - gameStore.currentDay
  if (daysLeft <= 0) return 'text-red-600'
  if (daysLeft <= 2) return 'text-yellow-600'
  return applyThemeClasses('text.secondary')
}

const getBidActivityMessage = (bid) => {
  switch (bid.status) {
    case 'accepted':
      return `Sold ${bid.chocoboName} to ${bid.bidderName} for ${bid.amount.toLocaleString()}G`
    case 'rejected':
      return `Rejected ${bid.bidderName}'s bid of ${bid.amount.toLocaleString()}G for ${bid.chocoboName}`
    case 'expired':
      return `Bid from ${bid.bidderName} for ${bid.chocoboName} expired (${bid.amount.toLocaleString()}G)`
    default:
      return `New bid from ${bid.bidderName} for ${bid.chocoboName}`
  }
}

const formatNotificationDate = (day) => {
  if (!day) return { date: 'N/A', daysAgo: 0 }
  
  const formattedDate = GaiaCalendar.formatGameDate(day)
  const daysAgo = gameStore.currentDay - day
  
  return {
    date: formattedDate,
    daysAgo: daysAgo
  }
}

const acceptBid = async (bid) => {
  isProcessing.value = true
  
  try {
    const success = BidManager.acceptBid(bid.id, gameStore)
    if (success) {
      // Refresh the view
      gameStore.saveGame()
    } else {
      alert('Failed to accept bid. The bidder may no longer have sufficient funds.')
    }
  } catch (error) {
    console.error('Error accepting bid:', error)
    alert('An error occurred while accepting the bid.')
  } finally {
    isProcessing.value = false
  }
}

const rejectBid = async (bid, markNotForSale = false) => {
  isProcessing.value = true
  showingRejectOptions.value = null // Close dropdown
  
  try {
    BidManager.rejectBid(bid.id, gameStore)
    
    // Mark chocobo as not for sale if requested
    if (markNotForSale) {
      const chocobo = gameStore.chocobos.find(c => c.id === bid.chocoboId)
      if (chocobo) {
        chocobo.notForSale = true
        
        // Reject all other pending bids for this chocobo
        gameStore.activeBids.forEach(otherBid => {
          if (otherBid.chocoboId === bid.chocoboId && 
              otherBid.id !== bid.id && 
              otherBid.status === 'pending') {
            BidManager.rejectBid(otherBid.id, gameStore)
          }
        })
      }
    }
    
    gameStore.saveGame()
  } catch (error) {
    console.error('Error rejecting bid:', error)
    alert('An error occurred while rejecting the bid.')
  } finally {
    isProcessing.value = false
  }
}

const showRejectOptions = (bid) => {
  showingRejectOptions.value = showingRejectOptions.value === bid.id ? null : bid.id
}

const viewChocobo = (chocoboId) => {
  emit('view-chocobo', chocoboId)
}

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (!event.target.closest('.relative')) {
    showingRejectOptions.value = null
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Custom scrollbar for activity list */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 2px;
}
</style>